import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Timeline_EventType, usePermissions } from '@tyro/api';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@tyro/core/dialog';
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFDateRangePicker,
  RHFSwitch,
  RHFTextField,
} from '@tyro/core/form';
import { useFormValidator } from '@tyro/core/hooks';
import { naturalSort } from '@tyro/core/utils';
import { useTranslation } from '@tyro/i18n';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { ReturnTypeFromUseNotes } from '../../../api/note/list';
import { useNoteTags } from '../../../api/note/note-tags';
import { useUpsertNote } from '../../../api/note/upsert-note';
import { useSaveTimelineHighlight } from '../../../api/student/timeline';
import {
  RHFStaffAutocomplete,
  type StaffSelectOption,
} from '../../common/staff-autocomplete';

type AdditionalHighlightState = {
  createHighlightFromNote: boolean;
  title: string;
  description: string;
  assignedTo: StaffSelectOption[];
};

type NoteFormState = NonNullable<ReturnTypeFromUseNotes> & {
  priorityDateRange?: [Dayjs, Dayjs];
  createHighlightFromNote: boolean;
  title: string;
  description: string;
  assignedTo: StaffSelectOption[];
} & AdditionalHighlightState;

export type UpsertNoteModalProps = {
  id: string;
  open: boolean;
  initialState: Partial<NoteFormState> | null;
  onClose: () => void;
  studentId: number | undefined;
};

const writeHighlightPermission = 'ps:1:student_timeline:write_highlights';

export const UpsertNoteModal = ({
  id,
  open,
  initialState,
  onClose,
  studentId,
}: UpsertNoteModalProps) => {
  const { t } = useTranslation(['common', 'people']);
  const { hasPermission } = usePermissions();
  const { mutate: createOrUpdateNoteMutation, isPending: isSubmitting } =
    useUpsertNote();
  const { data: noteTags } = useNoteTags({ enabled: open });
  const { mutate: createHighlight, isPending: isCreatingHighlight } =
    useSaveTimelineHighlight();

  const sortedNoteTags = useMemo(
    () => noteTags?.sort((a, b) => naturalSort(a.name, b.name)) ?? [],
    [noteTags],
  );

  const { resolver, rules } = useFormValidator<NoteFormState>();

  const { control, handleSubmit, watch, reset } = useForm<NoteFormState>({
    defaultValues: {
      ...initialState,
      priorityDateRange:
        initialState?.priorityStartDate && initialState?.priorityEndDate
          ? [
              dayjs(initialState?.priorityStartDate),
              dayjs(initialState?.priorityEndDate),
            ]
          : undefined,
      createHighlightFromNote: false,
    },
    resolver: resolver({
      note: rules.required(),
      tags: rules.required(),
      priorityDateRange: rules.required(),
      title: rules.required(),
      description: rules.required(),
      assignedTo: rules.required(),
    }),
  });

  const [createHighlightFromNote] = watch(['createHighlightFromNote']);

  const onSubmit = handleSubmit(
    ({ note, tags, priorityDateRange, ...restData }) => {
      const [startDate, endDate] = priorityDateRange || [null, null];

      createOrUpdateNoteMutation(
        [
          {
            ...restData,
            id: initialState?.id,
            note,
            tags: tags.map(({ id }) => id),
            referencedParties: [studentId!],
            priorityStartDate: startDate
              ? startDate.format('YYYY-MM-DD')
              : null,
            priorityEndDate: endDate ? endDate.format('YYYY-MM-DD') : null,
          },
        ],
        {
          onSuccess: (newNoteResponse) => {
            if (restData.createHighlightFromNote) {
              const { title, description, assignedTo } = restData;
              createHighlight(
                {
                  title,
                  highlight: description,
                  assignedToPartyIds: assignedTo.map(({ partyId }) => partyId),
                  studentPartyId: studentId!,
                  events: newNoteResponse.notes_upsertNotes.map(({ id }) => {
                    const eventType = priorityNote
                      ? Timeline_EventType.PriorityNote
                      : Timeline_EventType.Note;

                    return {
                      eventId: id!.toString(),
                      eventType,
                    };
                  }),
                },
                {
                  onSuccess: () => {
                    onClose();
                  },
                },
              );
            } else {
              onClose();
            }
          },
        },
      );
    },
  );

  useEffect(() => {
    if (open) {
      reset({
        ...initialState,
        priorityDateRange:
          initialState?.priorityStartDate && initialState?.priorityEndDate
            ? [
                dayjs(initialState?.priorityStartDate),
                dayjs(initialState?.priorityEndDate),
              ]
            : undefined,
      });
    }
  }, [open]);

  const priorityNote = watch('priorityNote');

  return (
    <Dialog
      id={id}
      open={open}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle onClose={onClose}>
        {initialState?.id ? t('people:editNote') : t('people:addNote')}
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Stack pt={1} spacing={3}>
            <RHFTextField
              label={t('people:note')}
              controlProps={{
                name: 'note',
                control,
              }}
              textFieldProps={{
                fullWidth: true,
                multiline: true,
                minRows: 3,
                autoFocus: true,
              }}
            />
            <RHFAutocomplete
              multiple
              label={t('common:label')}
              optionIdKey="id"
              optionTextKey="name"
              controlProps={{ name: 'tags', control }}
              options={sortedNoteTags}
              sx={{ mt: 2 }}
              renderTags={(tags, getTagProps) =>
                tags.map((tag, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      size="small"
                      variant="soft"
                      color={tag.colour}
                      label={tag.name}
                    />
                  );
                })
              }
            />

            {hasPermission('ps:1:wellbeing:write_priority_students') && (
              <>
                <RHFSwitch
                  label={t('people:markAsPriorityNote')}
                  controlProps={{ name: 'priorityNote', control }}
                  controlLabelProps={{ sx: { mt: 2 } }}
                />

                <Collapse
                  in={!!priorityNote}
                  unmountOnExit
                  sx={{
                    '&.MuiCollapse-root': {
                      marginTop: 1,
                    },
                  }}
                >
                  <RHFDateRangePicker
                    label={t('people:priorityDateRange')}
                    controlProps={{ name: 'priorityDateRange', control }}
                    textFieldProps={{
                      fullWidth: true,
                    }}
                  />
                </Collapse>
              </>
            )}

            {hasPermission(writeHighlightPermission) && (
              <>
                <RHFCheckbox
                  label={t('people:createHighlightFromNote')}
                  controlProps={{
                    name: 'createHighlightFromNote',
                    control,
                  }}
                />

                <Collapse in={createHighlightFromNote} unmountOnExit>
                  <Stack gap={3}>
                    <Typography component="h3" variant="subtitle1">
                      {t('people:highlightDetails')}
                    </Typography>
                    <RHFTextField
                      label={t('common:title')}
                      controlProps={{
                        name: 'title',
                        control,
                      }}
                    />
                    <RHFTextField
                      label={t('common:description')}
                      controlProps={{
                        name: 'description',
                        control,
                      }}
                      textFieldProps={{
                        fullWidth: true,
                        multiline: true,
                        rows: 4,
                      }}
                    />
                    <RHFStaffAutocomplete
                      label={t('people:assignedTo')}
                      multiple
                      controlProps={{
                        name: 'assignedTo',
                        control,
                      }}
                    />
                  </Stack>
                </Collapse>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="soft" color="inherit" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>

          <Button
            type="submit"
            variant="contained"
            loading={isSubmitting || isCreatingHighlight}
          >
            {t('common:actions.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
